import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { useImportDataOptions } from "@/hooks/use-import-data.hook.ts";
import {
  ApiErrorException,
  ApiValidationError,
  ApiValidationErrorField,
  ApiErrors,
} from "@/types/api.types.ts";
import { ImportDataEditStepMatchSkeleton } from "@/pages/settings/import-data/edit/components/ImportDataEditStepMatchSkeleton.tsx";
import { ImportDataMatchColumn } from "@/pages/settings/import-data/edit/components/ImportDataMatchColumn.tsx";
import {
  ColumnFormData,
  MatchFormData,
  useImportDataEditContext,
} from "@/pages/settings/import-data/edit/import-data-edit.types.ts";
import {
  importDataKeys,
  useImportDataParseByType,
  useImportDataParseField,
  useImportDataSave,
} from "@/queries/import-data.query.ts";
import {
  ApiImportDataType,
  ApiImportGlobalUpdateBehavior,
  ApiImportParse,
  ApiImportParsePossibleEntity,
  ApiImportPayloadSave,
  ApiImportNewValueBehavior,
} from "@/types/import-data.types.ts";
import {
  EmojiDisplay,
  ListItem,
  Multiselect,
  SelectItem,
  Tag,
  useToasts,
  Button,
  Stack,
  Text,
  Title,
} from "@skailer-com/uikit";
import { useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const importValidationErrorRules: Record<string, { requiredField: string }> = {
  import_position_date_from_required: {
    requiredField: "position_date_from",
  },
  import_employment_date_from_required: {
    requiredField: "employment_date_from",
  },
  import_compensation_date_from_required: {
    requiredField: "compensation_effective_date",
  },
  import_compensation_currency_required: {
    requiredField: "salary_currency",
  },
};

const importValidationErrorTypes = [
  ApiImportDataType.employee,
  ApiImportDataType.compensation,
  ApiImportDataType.additional_compensation,
  ApiImportDataType.position,
  ApiImportDataType.employment,
];

interface GlobalValidationError {
  column?: string;
  field?: string;
  requiredField: string;
  code: string;
}

interface MatchWarning {
  id: string;
  title: string;
}

export function ImportDataEditStepMatch() {
  const {
    importId,
    goToNextStep,
    registerOnNext,
    nextButtonText,
    setImportPayload,
    setNextLoading,
    importPayload,
    importType,
    importBehavior: selectedImportBehavior,
    matchingBy,
    selectedCustomTableId,
    selectedCustomTableName,
    defaultUpdateBehavior,
  } = useImportDataEditContext();
  const { data: parseOverview, isPending: isParseLoading } = useImportDataParseByType(
    importId,
    importType,
    selectedCustomTableId
  );
  const { mutate: parseField } = useImportDataParseField(importType, selectedCustomTableId);
  const saveMutation = useImportDataSave();
  const { onError } = useErrorHandle();
  const { t } = useTranslation();
  const { showInfo } = useToasts();
  const { getImportDataFieldTitle, getEmployeeFieldTitle, getFieldGroupTitle, getMatchSummary } =
    useImportDataOptions();

  const matchingByLabel =
    parseOverview?.columns.find((c) => c.column_index === matchingBy)?.header ?? "";

  const matchSummary = getMatchSummary({
    importType,
    customTableName: selectedCustomTableName ?? undefined,
    matchingByLabel,
    importBehavior: selectedImportBehavior,
  });

  const queryClient = useQueryClient();
  const [addedFieldsMap, setAddedFieldsMap] = useState<
    Record<string, { id: string; title: string }>
  >({});
  const [apiValidationErrors, setApiValidationErrors] = useState<ApiValidationErrorField[]>([]);

  const addNewFieldToCache = useCallback(
    (fieldKey: string, title: string) => {
      if (!importId || !importType) return;

      const newField: ApiImportParsePossibleEntity = {
        field_key: fieldKey,
        is_custom: true,
        title: title,
        group: undefined,
      };

      queryClient.setQueryData(
        importDataKeys.parseByType(importId, importType, selectedCustomTableId).queryKey,
        (oldData: ApiImportParse | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            possible_fields: [...(oldData.possible_fields ?? []), newField],
          };
        }
      );
    },
    [importId, importType, selectedCustomTableId, queryClient]
  );

  const requiredMessage = t("errors:field_required");
  const validationSchema = z.object({
    columns: z.array(z.custom<ColumnFormData>()).superRefine((columns, context) => {
      columns.forEach((column, index) => {
        if (!column.enabled) return;

        (["import_type", "as_field", "create_new_values"] as const).forEach((field) => {
          if (!column[field]) {
            context.addIssue({ code: "custom", path: [index, field], message: requiredMessage });
          }
        });
      });
    }),
    custom_tables_keys: z.array(
      z.object({
        table_id: z.number(),
        field_slugs: z.array(z.string().min(1, requiredMessage)).min(1, requiredMessage),
      })
    ),
  });

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<MatchFormData>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      columns: [],
      custom_tables_keys: [],
    },
  });

  const asFieldOptions: SelectItem[] =
    parseOverview?.possible_fields?.map((field) => ({
      id: field.field_key,
      title: getImportDataFieldTitle(importType, field),
      group: getFieldGroupTitle(field.group),
    })) ?? [];

  const validColumns = useMemo(
    () => parseOverview?.columns.filter((col) => col.sample_values.length > 0) ?? [],
    [parseOverview?.columns]
  );

  const isFormInitialized = useRef(false);

  useEffect(() => {
    if (!parseOverview || validColumns.length === 0 || isFormInitialized.current) {
      return;
    }

    isFormInitialized.current = true;
    const columnsData: ColumnFormData[] = validColumns.map((column) => {
      const savedColumn = importPayload?.columns.find(
        (c) => c.column_index === column.column_index
      );
      const tableId =
        importType === ApiImportDataType.custom_table ? (selectedCustomTableId ?? null) : null;
      const firstField = parseOverview.possible_fields?.[0]?.field_key;

      return {
        column_index: column.column_index,
        import_type: importType,
        table_id: tableId,
        as_field: savedColumn?.as_field ?? column.as_field ?? firstField,
        update_behavior:
          savedColumn?.update_behavior ??
          (selectedImportBehavior === ApiImportGlobalUpdateBehavior.update_only
            ? defaultUpdateBehavior
            : column.update_behavior),
        create_new_values: savedColumn
          ? savedColumn.create_new_values
            ? ApiImportNewValueBehavior.create_new_values
            : ApiImportNewValueBehavior.skip_news_records
          : column.count_values_to_create > 0
            ? null
            : ApiImportNewValueBehavior.skip_news_records,
        enabled: !!savedColumn,
      };
    });

    const customTableKey =
      importType === ApiImportDataType.custom_table && selectedCustomTableId
        ? (importPayload?.custom_tables_keys.find(
            (key) => key.table_id === selectedCustomTableId
          ) ?? {
            table_id: selectedCustomTableId,
            field_slugs: [],
          })
        : undefined;

    reset({
      columns: columnsData,
      custom_tables_keys: customTableKey ? [customTableKey] : [],
    });
  }, [
    validColumns,
    importPayload,
    importType,
    selectedCustomTableId,
    selectedImportBehavior,
    defaultUpdateBehavior,
    parseOverview,
  ]);

  const columns = useWatch({ control, name: "columns" });
  const customTableKey = useWatch({ control, name: "custom_tables_keys.0" });
  const customTableFieldOptions = useMemo<SelectItem[]>(() => {
    const enabledFieldKeys = new Set(
      columns
        ?.filter(
          (column) =>
            column.enabled &&
            column.import_type === ApiImportDataType.custom_table &&
            column.as_field
        )
        .map((column) => column.as_field)
    );

    return (
      parseOverview?.possible_fields
        ?.filter((field) => enabledFieldKeys.has(field.field_key))
        .map((field) => ({
          id: field.field_key,
          title: field.title,
        })) ?? []
    );
  }, [columns, parseOverview?.possible_fields]);

  const toggleColumn = useCallback(
    (index: number) => {
      const current = columns?.[index];
      if (current) {
        const newEnabled = !current.enabled;
        setValue(`columns.${index}.enabled`, newEnabled);

        if (newEnabled && current.as_field) {
          parseField({
            import_id: importId ?? 0,
            column_index: current.column_index,
            import_type: current.import_type,
            as_field: current.as_field,
          });
        }
      }
    },
    [columns, importId, parseField]
  );

  const scrollToFirstError = useCallback(() => {
    const firstErrorElement = document.querySelector('[data-error="true"]');
    if (firstErrorElement) {
      firstErrorElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, []);

  const globalValidationErrors = useMemo<GlobalValidationError[]>(() => {
    const result: GlobalValidationError[] = [];
    const usedErrorCodes = new Set<string>();

    for (const error of apiValidationErrors) {
      if (usedErrorCodes.has(error.code)) continue;

      const rule = importValidationErrorRules[error.code];
      if (!rule) continue;

      const hasRequiredField = columns?.some(
        (col) =>
          col.enabled &&
          importValidationErrorTypes.includes(col.import_type) &&
          col.as_field === rule.requiredField
      );

      if (hasRequiredField) continue;

      const affectedColumn = columns?.find(
        (col) =>
          col.enabled &&
          importValidationErrorTypes.includes(col.import_type) &&
          col.as_field === error.field
      );
      const affectedColumnHeader = validColumns.find(
        (vc) => vc.column_index === affectedColumn?.column_index
      )?.header;

      result.push({
        column: affectedColumnHeader,
        field: affectedColumn?.as_field,
        requiredField: rule.requiredField,
        code: error.code,
      });
      usedErrorCodes.add(error.code);
    }

    return result;
  }, [apiValidationErrors, columns, validColumns]);

  const matchWarnings = useMemo<MatchWarning[]>(() => {
    const warnings: MatchWarning[] = [];

    validColumns.forEach((column, index) => {
      const formColumn = columns?.[index];
      if (!formColumn?.enabled) return;

      const hasInvalidValues = Boolean(column.invalid_values?.count);
      const isSalaryCurrency = formColumn.as_field === "salary_currency";

      if (isSalaryCurrency && hasInvalidValues && !formColumn.fallback_currency) {
        warnings.push({
          id: `currency-${column.column_index}`,
          title: `${column.header}: ${t("import_data:unrecognized_currencies")}`,
        });
      } else if (!isSalaryCurrency && hasInvalidValues) {
        warnings.push({
          id: `invalid-${column.column_index}`,
          title: `${column.header}: ${t("import_data:invalid_values_found")}`,
        });
      }

      if (column.count_values_to_create > 0 && !formColumn.create_new_values) {
        warnings.push({
          id: `new-values-${column.column_index}`,
          title: `${column.header}: ${t("import_data:new_values_detected")}`,
        });
      }
    });

    globalValidationErrors.forEach((error) => {
      warnings.push({
        id: `validation-${error.code}`,
        title:
          error.column && error.field
            ? t("import_data:validation_error_add_field", {
                column: error.column,
                field: getEmployeeFieldTitle(error.field),
                required_field: getEmployeeFieldTitle(error.requiredField),
              })
            : t("import_data:validation_error_missing_field", {
                field: getEmployeeFieldTitle(error.requiredField),
              }),
      });
    });

    return warnings;
  }, [columns, globalValidationErrors, t, validColumns]);

  const onSubmit = handleSubmit(
    async (data: MatchFormData) => {
      if (!importId) return;

      const enabledColumns = Object.values(data.columns).filter((col) => col.enabled);

      if (enabledColumns.length === 0) {
        showInfo({ title: t("import_data:select_at_least_one_column") });
        return;
      }

      const customTablesKeysPayload =
        importType === ApiImportDataType.custom_table && selectedCustomTableId
          ? [
              {
                table_id: selectedCustomTableId,
                field_slugs: data.custom_tables_keys[0]?.field_slugs ?? [],
              },
            ]
          : [];

      const payload: ApiImportPayloadSave = {
        import_id: importId,
        import_behavior: selectedImportBehavior,
        matching_by: matchingBy ?? 0,
        columns: enabledColumns.map((col) => ({
          column_index: col.column_index,
          import_type: col.import_type,
          as_field: col.as_field,
          update_behavior: col.update_behavior,
          create_new_values: col.create_new_values === ApiImportNewValueBehavior.create_new_values,
          table_id: col.table_id,
          new_custom_field_name: col.new_custom_field_name,
          new_custom_field_type: col.new_custom_field_type,
          salary_period: col.salary_period,
          fallback_currency: col.fallback_currency,
        })),
        custom_tables_keys: customTablesKeysPayload,
      };

      setNextLoading(true);
      setApiValidationErrors([]);
      try {
        await saveMutation.mutateAsync(payload);
        setImportPayload(payload);
        goToNextStep();
      } catch (error) {
        let isImportValidationErrorHandled = false;

        if (error instanceof ApiErrorException) {
          const apiError = error.apiError;
          if (apiError?.status === ApiErrors.validationError) {
            const validationError = apiError as ApiValidationError;
            const importValidationErrors = validationError.validationErrors.filter(
              ({ code }) => importValidationErrorRules[code]
            );

            if (importValidationErrors.length > 0) {
              setApiValidationErrors(importValidationErrors);
              isImportValidationErrorHandled = true;
            }
          }
        }

        if (!isImportValidationErrorHandled) {
          onError(error as Error);
        }
      } finally {
        setNextLoading(false);
      }
    },
    () => {
      setTimeout(scrollToFirstError, 100);
    }
  );

  const handleNext = useCallback(() => {
    if (isParseLoading) {
      showInfo({ title: t("import_data:wait_for_parsing") });
      return;
    }

    onSubmit().then();
  }, [isParseLoading]);

  useLayoutEffect(() => {
    registerOnNext(handleNext);
    return () => registerOnNext(null);
  }, [handleNext, registerOnNext]);

  if (isParseLoading) {
    return <ImportDataEditStepMatchSkeleton />;
  }

  const handleAsFieldChange = (newAsField: string, index: number) => {
    const formColumn = columns?.[index];
    if (!formColumn) return;

    setValue(`columns.${index}.create_new_values`, null);

    parseField(
      {
        import_id: importId ?? 0,
        column_index: formColumn.column_index,
        import_type: formColumn.import_type,
        as_field: newAsField,
      },
      {
        onSuccess: (column) => {
          setValue(
            `columns.${index}.create_new_values`,
            column && column.count_values_to_create > 0
              ? null
              : ApiImportNewValueBehavior.skip_news_records
          );
        },
      }
    );
  };

  return (
    <Stack gap={"sm"}>
      <Stack className={"card-panel"}>
        <Stack gap={"2xl"}>
          <Title
            size={"lg"}
            header={t("import_data:check_data_matching")}
            caption={t("import_data:check_data_matching_caption")}
          />

          <Stack>
            <ListItem
              preTitle={t("import_data:importing_to")}
              title={matchSummary.values["import_type"]}
            />
            <ListItem
              preTitle={t("import_data:match_by")}
              title={matchSummary.values["match_by"]}
              className={"border-0"}
            />
          </Stack>
        </Stack>
      </Stack>

      {validColumns.length === 0 ? (
        <Stack className={"card-panel"}>
          <Text className={"text-secondary"}>{t("import_data:no_data_found_in_file")}</Text>
        </Stack>
      ) : (
        validColumns.map((column, index) => (
          <ImportDataMatchColumn
            key={`import-column-${index}`}
            index={index}
            column={column}
            control={control}
            isEnabled={columns?.[index]?.enabled ?? true}
            onToggle={() => toggleColumn(index)}
            formColumn={columns?.[index]}
            errors={errors}
            asFieldOptions={asFieldOptions}
            onAsFieldChange={(newValue) => handleAsFieldChange(newValue, index)}
            addedFieldsMap={addedFieldsMap}
            setAddedFieldsMap={setAddedFieldsMap}
            addNewFieldToCache={addNewFieldToCache}
            setValue={setValue}
            selectedImportBehavior={selectedImportBehavior}
          />
        ))
      )}

      {importType === ApiImportDataType.custom_table && selectedCustomTableId && customTableKey && (
        <>
          <Title
            size={"xs"}
            header={t("import_data:custom_tables_setup_title")}
            paddingTop
            paddingBottom
          />
          <Stack className={"card-panel"} gap={"2xl"}>
            <Title
              header={t("import_data:custom_tables_setup")}
              caption={t("import_data:custom_tables_setup_caption")}
            />
            <Stack gap={"md"}>
              <Stack
                direction={"horizontal"}
                gap={"sm"}
                items={"center"}
                className={"flex-wrap"}
                data-error={
                  errors?.custom_tables_keys?.[0]?.field_slugs?.message ? "true" : undefined
                }
              >
                <Text>{t("common:table")}</Text>
                <Text className={"font-semibold"}>{selectedCustomTableName ?? ""}</Text>
                <Text>{t("import_data:identify_by")}</Text>
                <Controller
                  control={control}
                  name={"custom_tables_keys.0.field_slugs"}
                  render={({ field: { value, onChange } }) => (
                    <Multiselect
                      values={value ?? []}
                      onChange={onChange}
                      options={customTableFieldOptions}
                      emptyTitle={t("common:select_fields")}
                      searchable
                      variant={"button"}
                    />
                  )}
                />
                {errors?.custom_tables_keys?.[0]?.field_slugs?.message && (
                  <Text className="text-danger text-sm">
                    {errors.custom_tables_keys[0]?.field_slugs?.message}
                  </Text>
                )}
              </Stack>
            </Stack>
          </Stack>
        </>
      )}

      {matchWarnings.length > 0 && (
        <Stack className={"card-panel"} gap={"2xl"} data-error="true">
          <Title size={"xs"} header={t("import_data:check_before_you_continue")} />
          <Stack gap={"sm"} items={"start"}>
            {matchWarnings.map((warning) => (
              <Tag
                key={warning.id}
                variant={"dark"}
                appearance={"outline"}
                className={"gap-2 text-base"}
              >
                <EmojiDisplay id={":warning:"} size={"xs"} />
                {warning.title}
              </Tag>
            ))}
          </Stack>
        </Stack>
      )}

      <Button size={"lg"} onClick={handleNext} isLoading={saveMutation.isPending}>
        {nextButtonText}
      </Button>
    </Stack>
  );
}
